// src/components/Layout/Layout.tsx
import "./style.scss";

import { FC, ReactNode, useContext, useEffect, useState } from "react";

import Aside from "../Aside/Aside";
import { useLocation } from "react-router-dom";
import DiagnosticModal from "features/diagnostic/pages/Diagnostic/components/Modal/DiagnosticModal";
import FloatingDiagnosticButton from "features/diagnostic/pages/Diagnostic/components/FloatingDiagnosticButton/FloatingDiagnosticButton";
import { useNavigate } from "react-router";
import { MFAProposalModal } from "features/mfa";
import { MFASetupModal } from "features/mfa";
import { UserContext } from "features/account";

type LayoutProps = {
    children: ReactNode;
};

const Layout: FC<LayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, getMe } = useContext(UserContext);
    const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
    const [hasSeenModal, setHasSeenModal] = useState(false);
    const [showMFAProposal, setShowMFAProposal] = useState(false);
    const [showMFASetup, setShowMFASetup] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Check for MFA proposal flag in localStorage or user data
    useEffect(() => {
        const checkMFAProposal = async () => {
            // Check if user is logged in and MFA is not enabled
            if ((user && !user.isEnableMFA) || (!localStorage.getItem('mfaProposalShown'))) {
                // Check if we should show the proposal
                const showProposal = localStorage.getItem('showMFAProposal');
                if (showProposal === 'true' && user?.isEnableMFA === false) {
                    setShowMFAProposal(true);
                    localStorage.removeItem('showMFAProposal'); // Clear after showing
                }
            }
        };

        if (!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0)) {
            checkMFAProposal();
        }
    }, [user]);

    useEffect(() => {
        // Check if the URL contains "professionnels"
        const hasProfessionnels = location.pathname.includes("professionnels");

        // Apply a class to the body element
        if (hasProfessionnels) {
            document.body.classList.add("professionnels");
        } else {
            document.body.classList.remove("professionnels");
        }

        // Show diagnostic modal on first visit to the Layout
        // Check if modal has already been shown (using localStorage)
        if (!hasSeenModal) {
            const modalShown = localStorage.getItem('diagnosticModalShown');

            // Only show if not shown before and not on professionnels page
            if (!modalShown && !hasProfessionnels) {
                // Small delay to ensure page is loaded
                const timer = setTimeout(() => {
                    setShowDiagnosticModal(true);
                }, 1000);

                return () => clearTimeout(timer);
            }
        }

        // Manage modal-open class on body
        if (showDiagnosticModal) {
            document.body.classList.add("modal-open");
        } else {
            document.body.classList.remove("modal-open");
        }

        // Cleanup function
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [location, showDiagnosticModal, hasSeenModal]);

    const handleCloseModal = () => {
        setShowDiagnosticModal(false);
        setHasSeenModal(true);
        localStorage.setItem('diagnosticModalShown', 'true');
    };

    const handleOpenModal = () => {
        setShowDiagnosticModal(true);
    };

    const handleAcceptMFA = () => {
        setShowMFAProposal(false);
        setShowMFASetup(true);
    };

    const handleCloseMFAProposal = () => {
        setShowMFAProposal(false);
        // Optionally set a flag to not show again for some time
        localStorage.setItem('mfaProposalDismissed', Date.now().toString());
    };

    const handleMFASetupSuccess = () => {
        setShowMFASetup(false);
        // Refresh user data to update MFA status
        if (getMe) {
            getMe();
        }
    };

    const handleCloseMFASetup = () => {
        setShowMFASetup(false);
    };

    return (
      <>
          <main>
              <Aside isCollapsed={isSidebarCollapsed} setIsCollapsed={setIsSidebarCollapsed} />
              <div className="content">{children}</div>
          </main>

          {/* Add the floating button - hide on professionnels pages */}
          {!(location.pathname.includes("professionnels") || (location.pathname.includes("id-assistant-ai"))) && (
            <FloatingDiagnosticButton onClick={handleOpenModal} />
          )}

          <DiagnosticModal
            isOpen={showDiagnosticModal}
            onClose={handleCloseModal}
          />

          {showMFAProposal && (
            <MFAProposalModal
              isOpen={showMFAProposal}
              onClose={handleCloseMFAProposal}
              onAccept={handleAcceptMFA}
            />
          )}

          {user && showMFASetup && (
            <MFASetupModal
              isOpen={showMFASetup}
              onClose={handleCloseMFASetup}
              onSuccess={handleMFASetupSuccess}
              userEmail={user.email || ''}
            />
          )}
      </>
    );
};

export default Layout;